import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { Sidebar } from '@/components/ui/sidebar'
import Header from '@/components/ui/header'
import BottomNav from '@/components/ui/bottom-nav'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { User, Globe, Users, Blocks, Lock, CreditCard, Loader2, Settings as SettingsIcon, ShieldUser, ShieldCheck, AlertTriangle, Building2, MessageSquare, Brain, BellRing, Bot } from 'lucide-react'
import Cookies from 'js-cookie'
import { getMyWorkspaces, type WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'
import ProfileSection from '@/features/settings/components/ProfileSection'
import WorkspaceSection from '@/features/settings/components/WorkspaceSection'
import PasswordSection from '@/features/settings/components/PasswordSection'
import BillingSection from '@/features/settings/components/BillingSection'
import IntegrationsSection from '@/features/integrations/components/IntegrationsSection'
import CrmPermissionsSection, { type CrmPermissionsSectionHandle } from '@/features/crm-permissions/components/CrmPermissionsSection'
import MemorySettingsSection from '@/features/settings/components/MemorySettingsSection'
import QualificationNotificationSettingsSection from '@/features/settings/components/QualificationNotificationSettingsSection'
import { CompanyProfileSection, type CompanyProfileSectionHandle } from '@/features/company-profile'
import { CommunicationGuidelinesSection, type CommunicationGuidelinesSectionHandle } from '@/features/communication-guidelines'
import { SettingsNav, type NavNode } from '@/features/settings/components/SettingsNav'
import type { LocalUser } from '@/features/settings/types'

const sections = [
  {
    id: 'profile',
    label: 'Personal Info',
    icon: User,
    desc: 'Your personal information visible to your workspace.',
  },
  {
    id: 'password',
    label: 'Password',
    icon: Lock,
    desc: 'Update your login password.',
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: CreditCard,
    desc: 'Manage your plan and payment details.',
  },
  {
    id: 'workspace',
    label: 'Workspace',
    icon: Users,
    desc: 'Manage your workspace details and team.',
  },
  {
    id: 'company-profile',
    label: 'Company Profile',
    icon: Building2,
    desc: 'Identity & positioning Zeph uses for research, qualification, and outreach.',
  },
  {
    id: 'memory',
    label: 'Memory',
    icon: Brain,
    desc: 'Control what Zeph remembers and reuses across conversations.',
  },
  {
    id: 'communication-guidelines',
    label: 'Communication Guidelines',
    icon: MessageSquare,
    desc: 'Brand voice, example emails, objection playbook, and follow-up cadence Zeph uses for outreach.',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Blocks,
    desc: 'Connect external tools to enhance your Zeph AI workspace.',
  },
  {
    id: 'permissions',
    label: 'Permissions',
    icon: ShieldCheck,
    desc: 'Control what Zeph is allowed to do on its own in your CRM and email.',
  },
  {
    id: 'qualification-notifications',
    label: 'Qualification Notifications',
    icon: BellRing,
    desc: 'Get notified when Zeph finishes qualifying a lead, and choose which outcomes to hear about.',
  },
]

const sectionsById = Object.fromEntries(sections.map((s) => [s.id, s]))

// Owner-only surface — hidden from the nav entirely for admins/members, not just
// uneditable (matches the backend guards, which also 403 non-owners on these).
const OWNER_ONLY_TAB_IDS = ['company-profile', 'communication-guidelines', 'permissions', 'billing']

// Owner-or-admin surface — same idea as OWNER_ONLY_TAB_IDS but also visible to
// admins (matches the backend's requireWorkspaceRole(['owner', 'admin']) guard
// on the memory-settings and qualification-notification-settings endpoints).
// 'memory' is intentionally NOT gated here (ticket 06): a member can't see the
// tier toggles (MemorySettingsSection itself hides those below owner/admin),
// but the tab must stay reachable so a member can review/delete their own
// facts (memory-architecture.md §6.2, spec story 20).
// 'qualification-notifications' has no such member-facing sub-view, so unlike
// memory it's fully gated: hidden from the nav for a member, matching the
// backend's owner/admin-only GET.
const OWNER_OR_ADMIN_TAB_IDS: string[] = ['qualification-notifications']

function buildNavNodes(isOwner: boolean, isOwnerOrAdmin: boolean): NavNode[] {
  const nodes: NavNode[] = [
    { type: 'group', id: 'profile-group', label: 'Profile', icon: User, children: ['profile', 'password'] },
    { type: 'leaf', id: 'integrations' },
  ]
  if (isOwner) {
    nodes.push({
      type: 'group',
      id: 'workspace-group',
      label: 'Workspace',
      icon: Globe,
      children: ['company-profile', 'workspace', 'billing'],
    })
    nodes.push({
      type: 'group',
      id: 'ai-config-group',
      label: 'AI Configuration',
      icon: Bot,
      children: ['communication-guidelines', 'memory', 'permissions', 'qualification-notifications'],
    })
  } else if (isOwnerOrAdmin) {
    nodes.push({ type: 'leaf', id: 'workspace' })
    nodes.push({ type: 'leaf', id: 'memory' })
    nodes.push({ type: 'leaf', id: 'qualification-notifications' })
  } else {
    nodes.push({ type: 'leaf', id: 'workspace' })
    // A member can't change memory settings, but still needs the tab to
    // review/delete their own remembered facts (ticket 06).
    nodes.push({ type: 'leaf', id: 'memory' })
  }
  return nodes
}

type PendingAction =
  | { kind: 'tab'; id: string }
  | { kind: 'admin' }
  | { kind: 'link'; href: string }

function isSuperAdmin(): boolean {
  try {
    const token = Cookies.get('token')
    if (!token) return false
    const segment = token.split('.')[1]
    if (!segment) return false
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>
    return payload.platformRole === 'super_admin'
  } catch {
    return false
  }
}

const SettingsPage: React.FC = () => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('gmail') || params.has('outlook')) return 'integrations'
    const tab = params.get('tab')
    return sections.some((s) => s.id === tab) ? tab! : 'profile'
  })
  const [user, setUser] = useState<LocalUser | null>(null)
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null)
  const showAdminLink = isSuperAdmin()
  const isOwner = workspace?.role === 'owner'
  const isOwnerOrAdmin = isOwner || workspace?.role === 'admin'
  const visibleSections = sections.filter((s) => {
    if (OWNER_ONLY_TAB_IDS.includes(s.id)) return isOwner
    if (OWNER_OR_ADMIN_TAB_IDS.includes(s.id)) return isOwnerOrAdmin
    return true
  })
  const activeSection = visibleSections.find((s) => s.id === active) ?? visibleSections[0]!
  const navNodes = buildNavNodes(isOwner, isOwnerOrAdmin)

  const permissionsRef = useRef<CrmPermissionsSectionHandle>(null)
  const [permissionsDirty, setPermissionsDirty] = useState(false)
  const companyProfileRef = useRef<CompanyProfileSectionHandle>(null)
  const [companyProfileDirty, setCompanyProfileDirty] = useState(false)
  const communicationGuidelinesRef = useRef<CommunicationGuidelinesSectionHandle>(null)
  const [communicationGuidelinesDirty, setCommunicationGuidelinesDirty] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const hasUnsavedPermissions = active === 'permissions' && permissionsDirty
  const hasUnsavedCompanyProfile = active === 'company-profile' && companyProfileDirty
  const hasUnsavedCommunicationGuidelines = active === 'communication-guidelines' && communicationGuidelinesDirty
  const hasUnsavedChanges = hasUnsavedPermissions || hasUnsavedCompanyProfile || hasUnsavedCommunicationGuidelines
  const unsavedChangesLabel = hasUnsavedPermissions
    ? 'AI Permissions'
    : hasUnsavedCompanyProfile
      ? 'Company Profile'
      : 'Communication Guidelines'

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user')
      if (raw) setUser(JSON.parse(raw))
    } catch {}
    getMyWorkspaces().then((ws) => { if (ws[0]) setWorkspace(ws[0]) }).catch(() => {})
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.has('gmail') || params.has('outlook')) { setActive('integrations'); return }
    const tab = params.get('tab')
    if (tab && sections.some((s) => s.id === tab)) setActive(tab)
  }, [location.search])

  // Guards direct URL access (?tab=company-profile, ?tab=permissions, ?tab=billing, ?tab=memory) by
  // a role that shouldn't see it — the nav already hides these, this catches anyone who lands on
  // one another way.
  useEffect(() => {
    if (!workspace) return
    const owner = workspace.role === 'owner'
    const ownerOrAdmin = owner || workspace.role === 'admin'
    if (!owner && OWNER_ONLY_TAB_IDS.includes(active)) {
      setActive('profile')
    } else if (!ownerOrAdmin && OWNER_OR_ADMIN_TAB_IDS.includes(active)) {
      setActive('profile')
    }
  }, [active, workspace])

  // Browser-level exits (refresh, close tab, type a new URL) while CRM Permissions
  // or Company Profile has unsaved changes — the browser shows its own generic
  // confirmation here, since custom text in beforeunload dialogs isn't supported
  // by modern browsers.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedChanges])

  // In-app navigation away (sidebar links, breadcrumbs, etc.) while CRM Permissions
  // or Company Profile has unsaved changes — intercepted at the document level
  // since react-router's useBlocker requires a data router and this app uses a
  // plain BrowserRouter.
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handler = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      const href = anchor?.getAttribute('href')
      if (!href || href.startsWith('#')) return
      e.preventDefault()
      e.stopPropagation()
      setPendingAction({ kind: 'link', href })
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [hasUnsavedChanges])

  const proceedWith = (action: PendingAction) => {
    if (action.kind === 'tab') setActive(action.id)
    else if (action.kind === 'admin') navigate('/admin')
    else if (action.kind === 'link') {
      if (action.href.startsWith('/')) navigate(action.href)
      else window.location.href = action.href
    }
  }

  const handleTabClick = (id: string) => {
    if (hasUnsavedChanges && id !== active) {
      setPendingAction({ kind: 'tab', id })
      return
    }
    setActive(id)
  }

  const handleAdminClick = () => {
    if (hasUnsavedChanges) {
      setPendingAction({ kind: 'admin' })
      return
    }
    navigate('/admin')
  }

  const handleConfirmSave = async () => {
    const action = pendingAction
    if (!action) return
    if (hasUnsavedPermissions) await permissionsRef.current?.save()
    else if (hasUnsavedCompanyProfile) await companyProfileRef.current?.save()
    else if (hasUnsavedCommunicationGuidelines) await communicationGuidelinesRef.current?.save()
    setPendingAction(null)
    proceedWith(action)
  }

  const handleConfirmDiscard = () => {
    const action = pendingAction
    if (!action) return
    if (hasUnsavedPermissions) permissionsRef.current?.discard()
    else if (hasUnsavedCompanyProfile) companyProfileRef.current?.discard()
    else if (hasUnsavedCommunicationGuidelines) communicationGuidelinesRef.current?.discard()
    setPendingAction(null)
    proceedWith(action)
  }

  return (
    <div className="h-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: t.primary }}>
      <div className="flex flex-1 min-h-0 overflow-hidden pb-16 md:pb-0">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header title="Settings" icon={<SettingsIcon size={20} />} />

        <main className="flex-1 flex min-h-0 overflow-hidden">
          <aside
            className="hidden md:flex md:flex-col md:w-72 md:shrink-0 gap-1.5 p-4 pr-5 overflow-y-auto custom-scrollbar [scrollbar-gutter:stable]"
            style={{ backgroundColor: t.secondary }}
          >
            <SettingsNav nodes={navNodes} sectionsById={sectionsById} active={active} onSelect={handleTabClick} />
          </aside>

          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-6 md:px-10 py-6 md:py-8 [scrollbar-gutter:stable]">
            <div className="max-w-4xl mx-auto">
              {/* Nav shown inline on mobile, where the persistent aside is hidden */}
              <div className="md:hidden mb-6">
                <SettingsNav nodes={navNodes} sectionsById={sectionsById} active={active} onSelect={handleTabClick} />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6 mb-6">
              <div>
                <h1 className="text-2xl font-bold" style={{ color: t.text }}>{activeSection.label}</h1>
                <p className="text-sm mt-1" style={{ color: t.mutedText }}>
                  {activeSection.desc}
                </p>
              </div>

              <div className="flex items-center gap-1 shrink-0 pt-1">
                {showAdminLink && (
                  <button
                    onClick={handleAdminClick}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium transition-colors"
                    style={{ color: t.secondrytext }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = t.hoverBackground
                      e.currentTarget.style.color = t.text
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                      e.currentTarget.style.color = t.secondrytext
                    }}
                  >
                    <ShieldUser size={14} strokeWidth={1.5} />
                    Admin panel
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl p-4 sm:p-7" style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}>
              {active === 'profile' && <ProfileSection user={user} workspaceName={workspace?.name} />}

              {active === 'password' && <PasswordSection />}

              {active === 'billing' && <BillingSection />}

              {active === 'workspace' && (
                workspace
                  ? <WorkspaceSection workspace={workspace} />
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'company-profile' && (
                workspace
                  ? (
                    <CompanyProfileSection
                      ref={companyProfileRef}
                      workspace={workspace}
                      onDirtyChange={setCompanyProfileDirty}
                    />
                  )
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'communication-guidelines' && (
                workspace
                  ? (
                    <CommunicationGuidelinesSection
                      ref={communicationGuidelinesRef}
                      workspace={workspace}
                      onDirtyChange={setCommunicationGuidelinesDirty}
                    />
                  )
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'memory' && (
                workspace
                  ? <MemorySettingsSection workspaceId={workspace.id} role={workspace.role} />
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'qualification-notifications' && (
                workspace
                  ? <QualificationNotificationSettingsSection workspaceId={workspace.id} role={workspace.role} />
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'integrations' && (
                workspace
                  ? <IntegrationsSection workspaceId={workspace.id} />
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}

              {active === 'permissions' && (
                workspace
                  ? (
                    <CrmPermissionsSection
                      ref={permissionsRef}
                      workspaceId={workspace.id}
                      canManage={workspace.role === 'owner'}
                      onDirtyChange={setPermissionsDirty}
                    />
                  )
                  : <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} /></div>
              )}
            </div>
          </div>
          </div>
        </main>
      </div>
      </div>

      <Dialog open={pendingAction !== null} onOpenChange={(open) => { if (!open) setPendingAction(null) }}>
        <DialogContent
          className="p-7 sm:p-8 rounded-2xl shadow-xl max-w-md w-[92vw] border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <div className="flex flex-col items-center text-center gap-4">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-full shrink-0"
              style={{ backgroundColor: t.warningBackground }}
            >
              <AlertTriangle size={22} style={{ color: t.warningText }} />
            </div>

            <DialogHeader className="items-center gap-2">
              <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
                Save changes to {unsavedChangesLabel}?
              </DialogTitle>
              <p className="text-sm leading-relaxed text-center" style={{ color: t.mutedText }}>
                You have unsaved changes here. Leaving now will discard them unless you save first.
              </p>
            </DialogHeader>
          </div>

          <DialogFooter className="flex flex-col-reverse gap-2.5 mt-7 sm:flex-row">
            <Button
              variant="secondary"
              onClick={() => setPendingAction(null)}
              className="flex-1 h-10 rounded-lg font-medium text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDiscard}
              className="flex-1 h-10 rounded-lg font-medium text-sm bg-red-500 hover:bg-red-600 text-white"
            >
              Discard
            </Button>
            <Button
              onClick={() => void handleConfirmSave()}
              className="flex-1 h-10 rounded-lg font-medium text-sm text-white"
              style={{ background: t.accentGradient }}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  )
}

export default SettingsPage
